import {useState, useMemo, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import './FboCustomization.scss';
import {useSelector} from 'react-redux';

const FBO_NOTIFICATIONS = [
  {
    key: 'notify_corrective_action_created',
    title: 'Corrective Action Created',
    description:
      'Triggered when a corrective action is created following a non-conformance identified during an inspection.',
  },
  {
    key: 'notify_corrective_action_closed',
    title: 'Corrective Action Closed',
    description:
      'Triggered when a corrective action has been completed and closed.',
  },
  {
    key: 'notify_differential_pressure_alert',
    title: 'Differential Pressure Alert',
    description:
      'Triggered when a differential pressure (DP) reading exceeds the acceptable range.',
    alert: true,
  },
  {
    key: 'notify_equipment_decommissioned',
    title: 'Equipment Decommissioned',
    description:
      'Triggered when equipment is decommissioned, either automatically (for example, due to inspection results or missed inspections) or manually by a user.',
  },
  {
    key: 'notify_missed_inspections',
    title: 'Missed Inspections',
    description: 'Triggered when inspections are missed.',
  },
  {
    key: 'notify_nozzle_fueling_pressure_alert',
    title: 'Nozzle Fueling Pressure Alert',
    description:
      'Triggered when a nozzle fueling pressure reading is outside the acceptable range.',
    alert: true,
  },
  {
    key: 'notify_partial_inspections',
    title: 'Partial Inspections',
    description:
      'Triggered when an inspection is started but not fully completed.',
  },
  {
    key: 'notify_sump_sample_threshold',
    title: 'Sump Sample Threshold',
    description:
      'Triggered when a sump sample reading exceeds the configured threshold value. Thresholds are configured in the FBO settings.',
    alert: true,
  },
  {
    key: 'notify_tank_condition_alert',
    title: 'Tank Condition Alert',
    description:
      'Triggered when a tank condition reading is outside the acceptable range.',
    alert: true,
  },
  {
    key: 'notify_upcoming_calibrations',
    title: 'Upcoming Calibration Date',
    description: 'Calibration due in the next 30 days.',
  },
  {
    key: 'notify_upcoming_oos',
    title: 'Upcoming Out of Service',
    description:
      'Triggered when equipment across multiple FBOs is approaching an out-of-service condition. This notification provides a consolidated summary of equipment that will soon be taken out of service.',
  },
];

const Toggle = ({checked, onChange}) => (
  <button
    type='button'
    className={`fbo-toggle${checked ? ' fbo-toggle--on' : ''}`}
    onClick={() => onChange(!checked)}
    aria-checked={checked}
    role='switch'
  >
    <span className='fbo-toggle__thumb' />
  </button>
);

const FboCustomization = ({
  agencies = [],
  defaults = {},
  onUpdate,
  onApplyToAll,
}) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [modes, setModes] = useState({});
  const [expanded, setExpanded] = useState({});
  const [prefs, setPrefs] = useState({});
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const {is_corporate, is_regional_corporate} = userInfo;
  const hideFboMultiple = is_corporate || is_regional_corporate;

  useEffect(() => {
    if (!agencies.length) return;
    setModes(
      Object.fromEntries(
        agencies.map(a => [
          a.agency_id,
          !hideFboMultiple
            ? 'override'
            : a.use_default
              ? 'default'
              : 'override',
        ]),
      ),
    );
    setExpanded(
      Object.fromEntries(
        agencies.map(a => [
          a.agency_id,
          !hideFboMultiple ? true : !a.use_default,
        ]),
      ),
    );
    setPrefs(
      Object.fromEntries(
        agencies.map(a => [a.agency_id, {...a.notifications}]),
      ),
    );
  }, [agencies]);

  const filtered = useMemo(
    () =>
      agencies.filter(
        a =>
          a.agency_title.toLowerCase().includes(search.toLowerCase()) ||
          a.agency_location.toLowerCase().includes(search.toLowerCase()),
      ),
    [agencies, search],
  );

  const saveCallbacks = revert => ({
    onSuccess: data => {
      queryClient.invalidateQueries(['notificationPreferences']);
      toast.success(data?.message ?? 'Preferences saved');
    },
    onError: error => {
      revert();
      toast.error(
        error?.response?.data?.message ?? 'Failed to save preferences',
      );
    },
  });

  const setMode = (agencyId, mode) => {
    const prevModes = modes;
    const prevExpanded = expanded;
    const prevPrefs = prefs;
    const revert = () => {
      setModes(prevModes);
      setExpanded(prevExpanded);
      setPrefs(prevPrefs);
    };

    if (mode === 'default') {
      setModes(prev => ({...prev, [agencyId]: 'default'}));
      setExpanded(prev => ({...prev, [agencyId]: false}));
      setPrefs(prev => ({...prev, [agencyId]: {...defaults}}));
      onUpdate?.(
        {
          agencies: [
            {agency_id: agencyId, use_default: true, notifications: null},
          ],
        },
        saveCallbacks(revert),
      );
    } else {
      const notifications = {...defaults};
      setModes(prev => ({...prev, [agencyId]: 'override'}));
      setExpanded(prev => ({...prev, [agencyId]: true}));
      setPrefs(prev => ({...prev, [agencyId]: notifications}));
      onUpdate?.(
        {agencies: [{agency_id: agencyId, use_default: false, notifications}]},
        saveCallbacks(revert),
      );
    }
  };

  const toggleExpand = id => {
    if (modes[id] !== 'override') return;
    setExpanded(prev => ({...prev, [id]: !prev[id]}));
  };

  const expandAll = () =>
    setExpanded(Object.fromEntries(agencies.map(a => [a.agency_id, true])));
  const collapseAll = () =>
    setExpanded(Object.fromEntries(agencies.map(a => [a.agency_id, false])));

  const togglePref = (agencyId, key) => {
    const prevPrefs = prefs;
    const notifications = {...prefs[agencyId], [key]: !prefs[agencyId][key]};
    setPrefs(prev => ({...prev, [agencyId]: notifications}));
    onUpdate?.(
      {agencies: [{agency_id: agencyId, use_default: false, notifications}]},
      saveCallbacks(() => setPrefs(prevPrefs)),
    );
  };

  const enableAll = agencyId => {
    const prevPrefs = prefs;
    const notifications = Object.fromEntries(
      FBO_NOTIFICATIONS.map(n => [n.key, true]),
    );
    setPrefs(prev => ({...prev, [agencyId]: notifications}));
    onUpdate?.(
      {agencies: [{agency_id: agencyId, use_default: false, notifications}]},
      saveCallbacks(() => setPrefs(prevPrefs)),
    );
  };

  const disableAll = agencyId => {
    const prevPrefs = prefs;
    const notifications = Object.fromEntries(
      FBO_NOTIFICATIONS.map(n => [n.key, false]),
    );
    setPrefs(prev => ({...prev, [agencyId]: notifications}));
    onUpdate?.(
      {agencies: [{agency_id: agencyId, use_default: false, notifications}]},
      saveCallbacks(() => setPrefs(prevPrefs)),
    );
  };

  const resetToDefaults = agencyId => {
    const prevPrefs = prefs;
    const notifications = {...defaults};
    setPrefs(prev => ({...prev, [agencyId]: notifications}));
    onUpdate?.(
      {agencies: [{agency_id: agencyId, use_default: false, notifications}]},
      saveCallbacks(() => setPrefs(prevPrefs)),
    );
  };

  const customCount = fboId =>
    Object.values(prefs[fboId] ?? {}).filter(Boolean).length;

  return (
    <div className='fbo-section'>
      <>
        {is_corporate && (
          <>
            <div className='fbo-section__header'>
              <div className='fbo-section__header-top'>
                <p className='fbo-section__title'>Per-FBO customization</p>
                {agencies.length > 0 && (
                  <button
                    className='notif-apply-all-btn'
                    onClick={onApplyToAll}
                  >
                    Use default for all FBOs
                  </button>
                )}
              </div>
              <p className='fbo-section__subtitle'>
                Each FBO inherits your defaults. Switch any FBO to{' '}
                <strong>Override</strong> to configure it individually.
              </p>
            </div>
          </>
        )}
      </>

      <>
        {hideFboMultiple && (
          <>
            <div className='fbo-toolbar'>
              <div className='fbo-search'>
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  className='fbo-search__icon'
                >
                  <circle
                    cx='11'
                    cy='11'
                    r='7'
                    stroke='#9CA3AF'
                    strokeWidth='2'
                  />
                  <path
                    d='M16.5 16.5L21 21'
                    stroke='#9CA3AF'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
                <input
                  className='fbo-search__input'
                  placeholder='Search FBOs by name or location...'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className='fbo-toolbar__actions'>
                <button className='fbo-text-btn' onClick={expandAll}>
                  Expand all
                </button>
                <button className='fbo-text-btn' onClick={collapseAll}>
                  Collapse all
                </button>
              </div>
            </div>
          </>
        )}
      </>

      <div className='fbo-list'>
        {filtered.map(agency => {
          const isOverride =
            !hideFboMultiple || modes[agency.agency_id] === 'override';
          const isExpanded = expanded[agency.agency_id];
          const count = customCount(agency.agency_id);

          return (
            <div
              key={agency.agency_id}
              className={`fbo-card${isOverride ? ' fbo-card--override' : ''}`}
            >
              <div
                className={`fbo-card__header${isOverride ? ' fbo-card__header--clickable' : ''}`}
                onClick={() => toggleExpand(agency.agency_id)}
              >
                <div className='fbo-card__left'>
                  <div
                    className='fbo-avatar'
                    style={{
                      background:
                        'linear-gradient(135deg, #4A90E2 0%, #2C5AA0 100%)',
                    }}
                  >
                    {agency.agency_location.slice(0, 3)}
                  </div>
                  <div className='fbo-card__info'>
                    <span className='fbo-card__name'>
                      {agency.agency_title}
                    </span>
                    <span className='fbo-card__location'>
                      {agency.agency_location}
                    </span>
                  </div>
                </div>

                <div
                  className='fbo-card__right'
                  onClick={e => e.stopPropagation()}
                >
                  {isOverride ? (
                    <span className='fbo-status fbo-status--custom'>
                      {count} of {FBO_NOTIFICATIONS.length} custom
                    </span>
                  ) : (
                    <span className='fbo-status fbo-status--default'>
                      Using defaults
                    </span>
                  )}
                  {!hideFboMultiple ? null : (
                    <div className='fbo-mode-group'>
                      <button
                        className={`fbo-mode-btn${!isOverride ? ' fbo-mode-btn--active' : ''}`}
                        onClick={() => setMode(agency.agency_id, 'default')}
                      >
                        Use Default
                      </button>
                      <button
                        className={`fbo-mode-btn${isOverride ? ' fbo-mode-btn--active' : ''}`}
                        onClick={() => setMode(agency.agency_id, 'override')}
                      >
                        Override
                      </button>
                    </div>
                  )}
                  {isOverride && (
                    <svg
                      className={`fbo-chevron${isExpanded ? ' fbo-chevron--open' : ''}`}
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={e => {
                        e.stopPropagation();
                        toggleExpand(agency.agency_id);
                      }}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='#6B7280'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div
                className={`fbo-card__body${isOverride && isExpanded ? ' fbo-card__body--open' : ''}`}
              >
                {isOverride && (
                  <div className='fbo-card__body-inner'>
                    <div className='fbo-card__body-bar'>
                      <span className='fbo-custom-badge'>
                        CUSTOM — {agency.agency_location}
                      </span>
                      <div className='fbo-card__body-actions'>
                        <button
                          className='fbo-text-btn'
                          onClick={() => enableAll(agency.agency_id)}
                        >
                          Enable all
                        </button>
                        <button
                          className='fbo-text-btn'
                          onClick={() => disableAll(agency.agency_id)}
                        >
                          Disable all
                        </button>
                        <button
                          className='fbo-text-btn'
                          onClick={() => resetToDefaults(agency.agency_id)}
                        >
                          Reset to defaults
                        </button>
                      </div>
                    </div>

                    <div className='fbo-notif-grid'>
                      {FBO_NOTIFICATIONS.map(notif => (
                        <div key={notif.key} className='fbo-notif-cell'>
                          <div className='fbo-notif-cell__text'>
                            <p className='fbo-notif-cell__title'>
                              {notif.title}
                              {notif.alert && (
                                <span className='fbo-alert-badge'>ALERT</span>
                              )}
                            </p>
                            <p className='fbo-notif-cell__desc'>
                              {notif.description}
                            </p>
                          </div>
                          <Toggle
                            checked={
                              prefs[agency.agency_id]?.[notif.key] ?? false
                            }
                            onChange={() =>
                              togglePref(agency.agency_id, notif.key)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FboCustomization;
